import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, ArrowRight, RefreshCcw, ArrowLeft, Loader2, Sparkles, Wallet } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const QuizPage = () => { 
    const { quizId } = useParams();
    const navigate = useNavigate();
    const [quiz, setQuiz] = useState(null);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [score, setScore] = useState(0);
    const [showScore, setShowScore] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [isAnswerCorrect, setIsAnswerCorrect] = useState(null);
    const [showExitModal, setShowExitModal] = useState(false);
    
    // 🚨 YENİ: Token kazanma durumunu tutan state
    const [earnedReward, setEarnedReward] = useState(false);

    // API'den quiz verilerini yükle
    useEffect(() => {
        const fetchQuiz = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/quizzes/${quizId}`);
                setQuiz(response.data);
                setCurrentQuestion(0);
                setScore(0);
                setShowScore(false);
                setSelectedAnswer(null);
                setIsAnswerCorrect(null);
                setEarnedReward(false); // State sıfırla
            } catch (error) {
                console.error('Quiz yüklenirken hata:', error);
                setQuiz(null);
            }
        };

        if (quizId) {
            fetchQuiz();
        }
    }, [quizId]);

    // Quiz sonucunu API'ye kaydet ve Token Kontrolü Yap
    const submitQuizResult = async (finalScore) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await axios.post(`${API_URL}/api/quizzes/${quizId}/submit`, 
                { score: finalScore }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // 🚨 EĞER BACKEND TOKEN VERDİYSE (Success %100 durumu)
            if (response.data.earned_token) {
                setEarnedReward(true);
            }

            setQuiz(prevQuiz => ({
                ...prevQuiz,
                completed: true,
                user_score: finalScore
            }));
            localStorage.setItem('quiz_completed', 'true');
            
        } catch (error) {
            if (error.response?.status === 400) {
                alert('Bu quizi zaten tamamladınız! Ödül sadece ilk tam başarıda verilir.');
                navigate('/quiz');
            } else {
                console.error('Quiz sonucu kaydedilirken hata:', error);
            }
        }
    };

    if (quiz === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-950 text-white font-inter py-20 md:py-32">
                <div className="text-center p-8 bg-gray-900 rounded-3xl border border-gray-800 shadow-2xl">
                    <Loader2 className="animate-spin w-12 h-12 text-blue-400 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-white mb-2">Quiz Yükleniyor...</h1>
                </div>
            </div>
        );
    }

    if (quiz === false || !quiz.questions || quiz.questions.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-950 text-white font-inter py-20 md:py-32">
                <div className="text-center p-8 bg-gray-900 rounded-3xl border border-gray-800 shadow-2xl">
                    <h1 className="text-3xl font-bold text-red-400 mb-4">Quiz Bulunamadı!</h1>
                    <Link to="/quiz" className="mt-6 inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-6 rounded-full transition-colors">Quiz Sayfasına Dön</Link>
                </div>
            </div>
        );
    }

    const handleAnswerClick = (isCorrect, answerText) => {
        if (selectedAnswer !== null) return;
        setSelectedAnswer(answerText);
        setIsAnswerCorrect(isCorrect);
        if (isCorrect) {
            setScore(prevScore => prevScore + 1);
        }
    };

    const handleGoBack = () => {
        if (!showScore && currentQuestion > 0) {
            setShowExitModal(true);
            return;
        }
        navigate("/quiz");
    };

    const handleNextQuestion = () => {
        const nextQuestion = currentQuestion + 1;
        if (nextQuestion < quiz.questions.length) {
            setCurrentQuestion(nextQuestion);
            setSelectedAnswer(null);
            setIsAnswerCorrect(null);
        } else {
            setShowScore(true);
            submitQuizResult(score);
        }
    };

    const handleRestartQuiz = () => {
        navigate('/quiz');
    };

    const cardVariants = {
        hidden: { opacity: 0, scale: 0.9, y: 50 },
        visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
        exit: { opacity: 0, scale: 0.9, y: -50, transition: { duration: 0.4 } },
    };

    const buttonVariants = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
    };

    const scoreVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: "easeOut" } },
    };

    return (
        <div className="relative min-h-screen bg-gray-950 text-white font-inter py-20 md:py-32 flex flex-col items-center justify-center overflow-hidden">
            <button
                onClick={handleGoBack}
                className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 rounded-full bg-gray-800 hover:bg-gray-700 text-white shadow-lg transition-colors z-10"
            >
                <ArrowLeft size={18} /> Quizlere Geri Dön
            </button>

            <motion.h1 
                className="text-5xl md:text-6xl font-extrabold mb-4 bg-gradient-to-r from-teal-400 via-indigo-500 to-purple-500 bg-clip-text text-transparent drop-shadow-md px-4 text-center"
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
            >
                {quiz.title}
            </motion.h1>
            <motion.p
                className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-16"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            >
                Tarih bilginizi test edin, Mekânname ile geçmişe doğru keyifli bir yolculuk yapın!
            </motion.p>
            <motion.div
                className="w-full max-w-2xl px-4 z-10"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
            >
                <div className="bg-gray-900/60 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-gray-800 shadow-2xl">
                    {showScore ? (
                        <motion.div 
                            className="text-center"
                            variants={scoreVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {/* 🚨 TOKEN KAZANMA ÖDÜL KARTI 🚨 */}
                            {earnedReward ? (
                                <motion.div 
                                    initial={{ scale: 0.5, opacity: 0 }} 
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="mb-8 p-6 bg-gradient-to-br from-yellow-500/20 to-amber-600/20 border border-yellow-500/50 rounded-2xl relative overflow-hidden shadow-[0_0_20px_rgba(234,179,8,0.2)]"
                                >
                                    <Sparkles className="absolute top-2 right-2 text-yellow-500 animate-pulse" />
                                    <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(234,179,8,0.4)]">
                                        <Wallet className="text-gray-950 w-10 h-10" />
                                    </div>
                                    <h2 className="text-3xl font-black text-yellow-400 mb-2 tracking-tight">KUSURSUZ ZAFER!</h2>
                                    <p className="text-white font-bold text-lg leading-tight">
                                        Hepsini doğru bildin ve <span className="text-yellow-400 underline decoration-2 underline-offset-4">100 Token</span> kazandın!
                                    </p>
                                </motion.div>
                            ) : (
                                <>
                                    <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                                    <h2 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-400 mb-4">
                                        Quiz Tamamlandı!
                                    </h2>
                                    <p className="text-2xl text-gray-200 mb-2">
                                        {quiz.questions.length} sorudan {score} tanesini doğru bildin!
                                    </p>
                            
                                </>
                            )}


                            <div className="w-full bg-gray-800 rounded-full h-4 mb-8 overflow-hidden border border-gray-700">
                                <motion.div 
                                    initial={{ width: 0 }} 
                                    animate={{ width: `${(score / quiz.questions.length) * 100}%` }}
                                    className={`h-full ${score === quiz.questions.length ? 'bg-yellow-500' : 'bg-indigo-500'}`}
                                />
                            </div>
                            
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleRestartQuiz}
                                className="flex items-center justify-center gap-2 mx-auto px-8 py-3 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition-all shadow-lg hover:shadow-indigo-500/30"
                            >
                                <RefreshCcw size={20} /> Diğer Quiz'leri Gör
                            </motion.button>
                        </motion.div>
                    ) : (
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentQuestion}
                                className="relative"
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                            >
                                <div className="text-left mb-6">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xl font-medium text-indigo-400">
                                            Soru {currentQuestion + 1} / {quiz.questions.length}
                                        </span>
                                        {score === currentQuestion && currentQuestion > 0 && (
                                            <span className="flex items-center gap-1 text-yellow-500 text-xs font-bold animate-pulse">
                                                <Sparkles size={14}/> Harika Gidiyorsun!
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-gray-100 mt-2">
                                        {quiz.questions[currentQuestion].question}
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {quiz.questions[currentQuestion].answers.map((answer) => (
                                        <motion.button
                                            key={answer.text}
                                            variants={buttonVariants}
                                            initial="initial"
                                            animate="animate"
                                            onClick={() => handleAnswerClick(answer.isCorrect, answer.text)}
                                            className={`
                                                relative w-full text-left px-6 py-4 rounded-xl border-2 transition-all duration-300 ease-in-out font-medium
                                                ${selectedAnswer === null ? 'bg-gray-800/50 border-gray-700 text-gray-200 hover:bg-indigo-700 hover:border-indigo-600' :
                                                    answer.isCorrect ? 'bg-green-600/20 border-green-500 text-green-100' :
                                                    selectedAnswer === answer.text && !isAnswerCorrect ? 'bg-red-600/20 border-red-500 text-red-100' :
                                                    'bg-gray-800/30 border-gray-700 opacity-50 text-gray-400'
                                                }
                                            `}
                                        >
                                            {selectedAnswer === answer.text && (
                                                <motion.div
                                                    className="absolute -top-3 -right-3 z-10"
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ type: "spring", stiffness: 500 }}
                                                >
                                                    {answer.isCorrect ? <CheckCircle size={28} className="text-green-400 fill-gray-900" /> : <XCircle size={28} className="text-red-400 fill-gray-900" />}
                                                </motion.div>
                                            )}
                                            {answer.text}
                                        </motion.button>
                                    ))}
                                </div>
                                {selectedAnswer !== null && (
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleNextQuestion}
                                        className="mt-8 w-full px-6 py-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition-all shadow-xl flex items-center justify-center gap-2"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                    >
                                        {currentQuestion === quiz.questions.length - 1 ? 'Sonuçları Gör' : 'Sonraki Soru'} <ArrowRight size={20} />
                                    </motion.button>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    )}
                </div>
            </motion.div>
            
            {/* Çıkış Modalı */}
            <AnimatePresence>
                {showExitModal && (
                    <motion.div 
                        className="fixed inset-0 flex items-center justify-center bg-black/80 z-[100] p-4 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div 
                            className="bg-gray-900 rounded-3xl p-8 max-w-sm w-full border border-gray-800 text-center shadow-2xl"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                        >
                            <h2 className="text-2xl font-bold text-red-400 mb-4">Macerayı Bitirme!</h2>
                            <p className="text-gray-400 mb-8 leading-relaxed">
                                Quizi tamamlamadan çıkarsan kazandığın başarılar ve olası token ödülleri silinecek.
                            </p>
                            <div className="flex gap-4">
                                <button onClick={() => navigate("/quiz")} className="flex-1 py-3 rounded-xl bg-gray-800 hover:bg-gray-700 font-bold transition-colors">Çıkış Yap</button>
                                <button onClick={() => setShowExitModal(false)} className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 font-bold transition-colors">Devam Et</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default QuizPage;